import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  BookOpen, ArrowDownLeft, ArrowUpRight, Calculator,
  Building2, Briefcase, Loader2, FileArchive,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDate } from '@/lib/api'

type Tab = 'entradas' | 'saidas' | 'apuracao' | 'ciap' | 'servicos'

async function baixarXmlsZip(ano: number, mes: number) {
  const t = toast.loading('Gerando ZIP dos XMLs...')
  try {
    const r = await api.get('/fiscal/livros/xmls-zip', { params: { ano, mes }, responseType: 'blob' })
    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }))
    const a = document.createElement('a')
    a.href = url; a.download = `xmls-${ano}${String(mes).padStart(2, '0')}.zip`; a.click()
    setTimeout(() => URL.revokeObjectURL(url), 60000)
    toast.success('ZIP gerado', { id: t })
  } catch (e: any) {
    let msg = 'Erro ao gerar o ZIP de XMLs'
    try { const j = JSON.parse(await e?.response?.data?.text?.()); if (j?.detail) msg = j.detail } catch {}
    toast.error(msg, { id: t })
  }
}

export default function FiscalLivrosPage() {
  const hoje = new Date()
  const [ano, setAno] = useState(hoje.getFullYear())
  const [mes, setMes] = useState(hoje.getMonth() + 1)
  const [tab, setTab] = useState<Tab>('apuracao')

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="text-brand-600" /> Livros Fiscais
          </h1>
          <p className="text-ink-600 text-sm">Registro de Entradas, Saídas e Apuração de ICMS.</p>
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-sm text-ink-600">Competência:</span>
          <select className="input" value={mes} onChange={e => setMes(Number(e.target.value))}>
            {Array.from({length: 12}, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
            ))}
          </select>
          <select className="input" value={ano} onChange={e => setAno(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      <div className="border-b flex gap-2 flex-wrap">
        {[
          { k: 'apuracao', label: 'Apuração ICMS', icon: Calculator },
          { k: 'entradas', label: 'Entradas', icon: ArrowDownLeft },
          { k: 'saidas', label: 'Saídas', icon: ArrowUpRight },
          { k: 'ciap', label: 'CIAP (Imobilizado)', icon: Building2 },
          { k: 'servicos', label: 'Serviços Tomados', icon: Briefcase },
        ].map(t => {
          const I = t.icon
          return (
            <button key={t.k} onClick={() => setTab(t.k as Tab)}
              className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
                tab === t.k ? 'border-brand-600 text-brand-700 font-semibold' : 'border-transparent text-ink-600 hover:text-ink-900'
              }`}>
              <I size={14} /> {t.label}
            </button>
          )
        })}
      </div>

      {tab === 'apuracao' && <ApuracaoTab ano={ano} mes={mes} />}
      {tab === 'entradas' && <EntradasTab ano={ano} mes={mes} />}
      {tab === 'saidas' && <SaidasTab ano={ano} mes={mes} />}
      {tab === 'ciap' && <CIAPTab ano={ano} mes={mes} />}
      {tab === 'servicos' && <ServicosTab ano={ano} mes={mes} />}
    </div>
  )
}

function CIAPTab({ ano, mes }: { ano: number; mes: number }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<any>({
    queryKey: ['ciap-apuracao', ano, mes],
    queryFn: async () => (await api.get('/fiscal/livros/ciap/apuracao', { params: { ano, mes } })).data,
  })
  const apropriar = useMutation({
    mutationFn: async () => (await api.post('/fiscal/livros/ciap/apropriar', null, { params: { ano, mes } })).data,
    onSuccess: (d) => { toast.success(`${d.apropriadas} parcela(s) apropriada(s)`); qc.invalidateQueries({ queryKey: ['ciap-apuracao'] }) },
  })
  if (isLoading || !data) return <div className="p-8 text-center">Carregando CIAP...</div>

  const pendentes = (data.parcelas || []).filter((p: any) => !p.apropriado_em).length

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card card-body">
          <div className="text-xs text-ink-500 uppercase">Parcelas na competência</div>
          <div className="text-2xl font-bold">{data.total_parcelas}</div>
        </div>
        <div className="card card-body">
          <div className="text-xs text-ink-500 uppercase">Crédito adicional ICMS</div>
          <div className="text-2xl font-bold text-emerald-700">{formatMoney(data.credito_total_periodo)}</div>
          <div className="text-xs text-ink-500 mt-1">somado ao crédito do mês</div>
        </div>
        <div className="card card-body flex flex-col justify-between">
          <div>
            <div className="text-xs text-ink-500 uppercase">Pendentes</div>
            <div className="text-2xl font-bold text-amber-700">{pendentes}</div>
          </div>
          {pendentes > 0 && (
            <button onClick={() => apropriar.mutate()} disabled={apropriar.isPending} className="btn-primary text-xs mt-2">
              {apropriar.isPending && <Loader2 size={12} className="animate-spin" />}
              Apropriar todas
            </button>
          )}
        </div>
      </div>

      <div className="table-wrap">
        <table className="table text-xs">
          <thead>
            <tr>
              <th>NF</th><th>Bem</th><th>Parcela</th>
              <th className="text-right">Crédito</th><th>Apropriada em</th>
            </tr>
          </thead>
          <tbody>
            {data.parcelas.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-ink-500">Nenhuma parcela CIAP nesta competência.</td></tr>}
            {data.parcelas.map((p: any) => (
              <tr key={p.parcela_id}>
                <td className="font-mono">#{p.nf_id} — {p.nf_numero || '-'}</td>
                <td>{p.descricao_bem || '-'}</td>
                <td>{p.numero_parcela}/{p.total_parcelas}</td>
                <td className="text-right font-mono text-emerald-700">{formatMoney(p.valor_credito)}</td>
                <td>{p.apropriado_em ? formatDate(p.apropriado_em.slice(0, 10)) : <span className="text-amber-700">pendente</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ServicosTab({ ano, mes }: { ano: number; mes: number }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['servicos-tomados', ano, mes],
    queryFn: async () => (await api.get('/fiscal/livros/servicos-tomados', { params: { ano, mes } })).data,
  })
  if (isLoading || !data) return <div className="p-8 text-center">Carregando...</div>

  return (
    <div className="space-y-3">
      <div className="text-sm text-ink-600">
        <strong>{data.total_documentos}</strong> NFS-e de serviços tomados no período.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="card card-body"><div className="text-xs text-ink-500">Valor bruto</div><div className="font-bold">{formatMoney(data.totais.servicos)}</div></div>
        <div className="card card-body"><div className="text-xs text-ink-500">ISS retido</div><div className="font-bold text-rose-700">{formatMoney(data.totais.iss_ret)}</div></div>
        <div className="card card-body"><div className="text-xs text-ink-500">IR retido</div><div className="font-bold text-rose-700">{formatMoney(data.totais.ir)}</div></div>
        <div className="card card-body"><div className="text-xs text-ink-500">INSS retido</div><div className="font-bold text-rose-700">{formatMoney(data.totais.inss)}</div></div>
      </div>
      <div className="table-wrap">
        <table className="table text-xs">
          <thead>
            <tr>
              <th>Data</th><th>NF</th><th>Fornecedor</th>
              <th className="text-right">Serviços</th>
              <th className="text-right">ISS</th><th className="text-right">ISS-ret</th>
              <th className="text-right">IR-ret</th><th className="text-right">INSS-ret</th>
              <th className="text-right">CSLL</th><th className="text-right">Líquido</th>
            </tr>
          </thead>
          <tbody>
            {data.linhas.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-ink-500">Sem serviços tomados.</td></tr>}
            {data.linhas.map((l: any) => (
              <tr key={l.id}>
                <td>{formatDate(l.data_emissao)}</td>
                <td className="font-mono">{l.numero}</td>
                <td>{l.fornecedor}</td>
                <td className="text-right">{formatMoney(l.valor_servicos)}</td>
                <td className="text-right">{formatMoney(l.valor_iss)}</td>
                <td className="text-right text-rose-700">{formatMoney(l.valor_iss_retido)}</td>
                <td className="text-right text-rose-700">{formatMoney(l.valor_ir_retido)}</td>
                <td className="text-right text-rose-700">{formatMoney(l.valor_inss_retido)}</td>
                <td className="text-right text-rose-700">{formatMoney(l.valor_csll_retido)}</td>
                <td className="text-right font-semibold">{formatMoney(l.valor_liquido)}</td>
              </tr>
            ))}
          </tbody>
          {data.linhas.length > 0 && (
            <tfoot>
              <tr className="font-bold bg-ink-50">
                <td colSpan={3} className="text-right">Totais:</td>
                <td className="text-right">{formatMoney(data.totais.servicos)}</td>
                <td className="text-right">{formatMoney(data.totais.iss)}</td>
                <td className="text-right text-rose-700">{formatMoney(data.totais.iss_ret)}</td>
                <td className="text-right text-rose-700">{formatMoney(data.totais.ir)}</td>
                <td className="text-right text-rose-700">{formatMoney(data.totais.inss)}</td>
                <td className="text-right text-rose-700">{formatMoney(data.totais.csll)}</td>
                <td className="text-right">{formatMoney(data.totais.liquido)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

function ApuracaoTab({ ano, mes }: { ano: number; mes: number }) {
  const [cumulativo, setCumulativo] = useState(true)
  const { data, isLoading } = useQuery<any>({
    queryKey: ['livro-apuracao', ano, mes, cumulativo],
    queryFn: async () => (await api.get('/fiscal/livros/apuracao', { params: { ano, mes, cumulativo } })).data,
  })
  if (isLoading || !data) return <div className="p-8 text-center">Carregando apuração...</div>

  const corSaldo = (t: string) => t === 'devedor' ? 'text-rose-700' : t === 'credor' ? 'text-emerald-700' : 'text-ink-600'
  const tributos = [
    { nome: 'ICMS', ap: data.apuracao_icms },
    { nome: 'PIS', ap: data.apuracao_pis },
    { nome: 'COFINS', ap: data.apuracao_cofins },
  ]
  const totalRecolher = tributos.reduce((s, t) => s + (t.ap.saldo > 0 ? t.ap.saldo : 0), 0)

  const baixarSpedTxt = async (rota: string, nome: string) => {
    try {
      const r = await api.get(`/fiscal/livros/sped/${rota}`, { params: { ano, mes }, responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/plain' }))
      const a = document.createElement('a')
      a.href = url; a.download = `${nome}-${ano}${String(mes).padStart(2, '0')}.txt`; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { toast.error(`Erro ao gerar ${nome}`) }
  }
  const baixarSped = () => baixarSpedTxt('efd-icms-ipi', 'EFD-ICMS-IPI')
  const baixarContrib = () => baixarSpedTxt('efd-contribuicoes', 'EFD-Contribuicoes')

  return (
    <div className="space-y-4">
      {/* Saldo por tributo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {tributos.map(({ nome, ap }) => (
          <div key={nome} className="card card-body">
            <div className="flex items-center gap-2 text-ink-500 text-xs uppercase mb-1">
              <Calculator size={14} /> {nome} — saldo ({ap.tipo_saldo})
            </div>
            <div className={`text-2xl font-bold ${corSaldo(ap.tipo_saldo)}`}>{formatMoney(Math.abs(ap.saldo))}</div>
            <div className="text-xs text-ink-500 mt-1">
              {ap.tipo_saldo === 'devedor' ? 'A recolher' : ap.tipo_saldo === 'credor' ? 'A compensar' : 'Neutro'}
            </div>
          </div>
        ))}
      </div>

      {/* Regime PIS/COFINS */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink-600">Regime PIS/COFINS:</span>
        <button onClick={() => setCumulativo(true)}
          className={`px-3 py-1 rounded-lg text-xs font-medium ${cumulativo ? 'bg-brand-600 text-white' : 'border border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
          Cumulativo (Lucro Presumido)
        </button>
        <button onClick={() => setCumulativo(false)}
          className={`px-3 py-1 rounded-lg text-xs font-medium ${!cumulativo ? 'bg-brand-600 text-white' : 'border border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
          Não-cumulativo (Lucro Real)
        </button>
        <span className="text-xs text-ink-400">{cumulativo ? 'sem crédito sobre entradas' : 'credita PIS/COFINS das entradas'}</span>
      </div>

      {/* Tabela de apuração por tributo */}
      <div className="card card-body">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="font-semibold text-sm uppercase">Apuração de {String(mes).padStart(2, '0')}/{ano}</h3>
          <div className="flex items-center gap-3">
            <div className="text-sm">Total a recolher: <span className="font-bold text-rose-700">{formatMoney(totalRecolher)}</span></div>
            <button onClick={baixarSped}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium">
              <BookOpen size={14} /> SPED Fiscal (ICMS/IPI)
            </button>
            <button onClick={baixarContrib}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium">
              <BookOpen size={14} /> EFD-Contribuições (PIS/COFINS)
            </button>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table text-sm">
            <thead>
              <tr>
                <th>Tributo</th>
                <th className="text-right">Débitos (saídas)</th>
                <th className="text-right">Créditos (entradas)</th>
                <th className="text-right">Aproveitável</th>
                <th className="text-right">Saldo</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {tributos.map(({ nome, ap }) => (
                <tr key={nome}>
                  <td className="font-medium">{nome}</td>
                  <td className="text-right font-mono text-rose-700">{formatMoney(ap.debitos)}</td>
                  <td className="text-right font-mono text-emerald-700">{formatMoney(ap.creditos)}</td>
                  <td className="text-right font-mono">{formatMoney(ap.creditos_aproveitaveis ?? ap.creditos)}</td>
                  <td className={`text-right font-mono font-bold ${corSaldo(ap.tipo_saldo)}`}>{formatMoney(Math.abs(ap.saldo))}</td>
                  <td>{ap.tipo_saldo === 'devedor' ? 'A recolher' : ap.tipo_saldo === 'credor' ? 'A compensar' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totais entradas/saídas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card card-body">
          <h3 className="font-semibold mb-3 text-sm uppercase">Totais de entradas ({data.entradas.qtd_documentos})</h3>
          <dl className="space-y-1 text-sm">
            <Linha label="Valor produtos" valor={data.entradas.valor_produtos} />
            <Linha label="BC ICMS" valor={data.entradas.valor_bc_icms} />
            <Linha label="ICMS crédito" valor={data.entradas.valor_icms_creditos} accent="emerald" />
            <Linha label="PIS" valor={data.entradas.valor_pis} accent="emerald" />
            <Linha label="COFINS" valor={data.entradas.valor_cofins} accent="emerald" />
            <Linha label="IPI" valor={data.entradas.valor_ipi} />
            <Linha label="Valor total" valor={data.entradas.valor_total} bold />
          </dl>
        </div>
        <div className="card card-body">
          <h3 className="font-semibold mb-3 text-sm uppercase">Totais de saídas ({data.saidas.qtd_documentos})</h3>
          <dl className="space-y-1 text-sm">
            <Linha label="BC ICMS" valor={data.saidas.valor_bc_icms} />
            <Linha label="ICMS débito" valor={data.saidas.valor_icms_debitos} accent="rose" />
            <Linha label="PIS" valor={data.saidas.valor_pis} accent="rose" />
            <Linha label="COFINS" valor={data.saidas.valor_cofins} accent="rose" />
            <Linha label="IPI" valor={data.saidas.valor_ipi} />
            <Linha label="Valor total" valor={data.saidas.valor_total} bold />
          </dl>
        </div>
      </div>
    </div>
  )
}

function Linha({ label, valor, accent, bold }: { label: string; valor: number; accent?: 'emerald' | 'rose'; bold?: boolean }) {
  const classe = accent === 'emerald' ? 'text-emerald-700' : accent === 'rose' ? 'text-rose-700' : ''
  return (
    <div className={`flex justify-between ${bold ? 'border-t pt-2 mt-1 font-bold' : ''}`}>
      <dt className="text-ink-600">{label}</dt>
      <dd className={`font-mono ${classe}`}>{formatMoney(valor)}</dd>
    </div>
  )
}

function EntradasTab({ ano, mes }: { ano: number; mes: number }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['livro-entradas', ano, mes],
    queryFn: async () => (await api.get('/fiscal/livros/entradas', { params: { ano, mes } })).data,
  })
  if (isLoading || !data) return <div className="p-8 text-center">Carregando...</div>

  return (
    <div className="space-y-3">
      <div className="text-sm text-ink-600">
        <strong>{data.total_documentos}</strong> NF(e)s de entrada no período.
      </div>
      <div className="table-wrap">
        <table className="table text-xs">
          <thead>
            <tr>
              <th>Data</th><th>Nº</th><th>Fornecedor</th><th>CFOP</th>
              <th className="text-right">Produtos</th>
              <th className="text-right">BC ICMS</th><th className="text-right">ICMS</th>
              <th className="text-right">IPI</th><th className="text-right">ICMS-ST</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.linhas.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-ink-500">Sem NFs no período.</td></tr>}
            {data.linhas.map((l: any) => (
              <tr key={l.id}>
                <td>{formatDate(l.data_emissao)}</td>
                <td className="font-mono">{l.numero || '-'}/{l.serie || '-'}</td>
                <td>{l.fornecedor_nome || '-'}</td>
                <td className="font-mono">{l.cfop || '-'}</td>
                <td className="text-right">{formatMoney(l.valor_produtos)}</td>
                <td className="text-right">{formatMoney(l.valor_bc_icms)}</td>
                <td className="text-right text-emerald-700">{formatMoney(l.valor_icms)}</td>
                <td className="text-right">{formatMoney(l.valor_ipi)}</td>
                <td className="text-right">{formatMoney(l.valor_icms_st)}</td>
                <td className="text-right font-semibold">{formatMoney(l.valor_total)}</td>
              </tr>
            ))}
          </tbody>
          {data.linhas.length > 0 && (
            <tfoot>
              <tr className="font-bold bg-ink-50">
                <td colSpan={4} className="text-right">Totais:</td>
                <td className="text-right">{formatMoney(data.totais.produtos)}</td>
                <td className="text-right">{formatMoney(data.totais.bc_icms)}</td>
                <td className="text-right text-emerald-700">{formatMoney(data.totais.icms)}</td>
                <td className="text-right">{formatMoney(data.totais.ipi)}</td>
                <td className="text-right">{formatMoney(data.totais.icms_st)}</td>
                <td className="text-right">{formatMoney(data.totais.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

function SaidasTab({ ano, mes }: { ano: number; mes: number }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['livro-saidas', ano, mes],
    queryFn: async () => (await api.get('/fiscal/livros/saidas', { params: { ano, mes } })).data,
  })
  if (isLoading || !data) return <div className="p-8 text-center">Carregando...</div>

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-ink-600">
          <strong>{data.total_documentos}</strong> NF(e)s autorizadas no período.
        </div>
        <button onClick={() => baixarXmlsZip(ano, mes)}
          title="Baixa um .zip com todos os XML (procNFe) gerados no período"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium">
          <FileArchive size={14} /> Baixar XMLs (ZIP)
        </button>
      </div>
      <div className="table-wrap">
        <table className="table text-xs">
          <thead>
            <tr>
              <th>Data</th><th>Nº</th><th>Cliente</th><th>Pedido</th><th>CFOP</th>
              <th className="text-right">BC ICMS</th><th className="text-right">ICMS</th>
              <th className="text-right">IPI</th><th className="text-right">ICMS-ST</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.linhas.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-ink-500">Sem NFs no período.</td></tr>}
            {data.linhas.map((l: any) => (
              <tr key={l.id}>
                <td>{formatDate(l.data_emissao)}</td>
                <td className="font-mono">{l.numero || '-'}/{l.serie || '-'}</td>
                <td>{l.cliente_nome || '-'}</td>
                <td className="text-xs">{l.pedido_numero ? `#${l.pedido_numero}` : '-'}</td>
                <td className="font-mono">{l.cfop || '-'}</td>
                <td className="text-right">{formatMoney(l.valor_bc_icms)}</td>
                <td className="text-right text-rose-700">{formatMoney(l.valor_icms)}</td>
                <td className="text-right">{formatMoney(l.valor_ipi)}</td>
                <td className="text-right">{formatMoney(l.valor_icms_st)}</td>
                <td className="text-right font-semibold">{formatMoney(l.valor_total)}</td>
              </tr>
            ))}
          </tbody>
          {data.linhas.length > 0 && (
            <tfoot>
              <tr className="font-bold bg-ink-50">
                <td colSpan={5} className="text-right">Totais:</td>
                <td className="text-right">{formatMoney(data.totais.bc_icms)}</td>
                <td className="text-right text-rose-700">{formatMoney(data.totais.icms)}</td>
                <td className="text-right">{formatMoney(data.totais.ipi)}</td>
                <td className="text-right">{formatMoney(data.totais.icms_st)}</td>
                <td className="text-right">{formatMoney(data.totais.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
